import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronRight, Minus, MoreHorizontal, Plus, Undo2 } from 'lucide-react'
import { apiErrorText } from '@/lib/api-error'
import { useTeilung } from '@/lib/teilung'
import { useTerminology } from '@/lib/useTerminology'
import { NOTE_COLOR_CLASSES } from '../../project-labels'
import {
  autoArrange,
  copyLayout,
  createCockpitGroup,
  deleteCockpitGroup,
  fetchLayoutOwners,
  placeCard,
  placeCards,
  restoreLayout,
  updateCockpitGroup,
  type CockpitFilter,
} from '../../api/work-cockpit.api'
import { releaseWorkSteps } from '../../api/projects-phase-d.api'
import type { AutoArrangeMode, CockpitBoard as Board, CockpitCard, CockpitGroup, CockpitLayoutSnapshot } from '../../types/cockpit.types'
import { CockpitCardView } from './CockpitCardView'
import { bestaetige, frage } from '@/lib/dialoge'

const FARBEN: CockpitGroup['color'][] = ['YELLOW', 'BLUE', 'GREEN', 'PINK', 'GREY']
const KOPF = 48
const KLEIN_H = 76
/** Abstand zum unteren Rand des Inhaltsbereichs — der Innenabstand der Seite (`pageWide`, `sm:p-6`). */
const UNTEN = 24
/** Overlays tragen ihre Farbe selbst (`.claude/rules/styling.md`). */
const OVERLAY = { background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }

type Ziehen =
  | { art: 'karte'; stepId: string; startX: number; startY: number; greifX: number; greifY: number; bewegt: boolean; x: number; y: number; ausEingang: boolean }
  | { art: 'gruppe'; id: string; startX: number; startY: number; x0: number; y0: number; dx: number; dy: number }
  | { art: 'groesse'; id: string; startX: number; startY: number; w0: number; h0: number; dw: number; dh: number }
  | { art: 'rahmen'; x0: number; y0: number; x1: number; y1: number }

/**
 * AP-35 Detailblatt 11 4.3 — **das Board.**
 *
 * Freie Fläche mit absoluten Koordinaten (kein Kanban-Framework, kein Canvas),
 * Eingang als fester Streifen links. Gezogen wird mit Pointer-Events — eine
 * Bibliothek für Ziehen gibt es im ERP nicht, und für eine Fläche mit freien
 * Koordinaten bräuchte es auch keine.
 *
 * **Gespeichert wird je Karte**, sobald sie losgelassen ist — kein Speichern
 * des ganzen Boards. Die Lage gehört dem Benutzer; die Einteilung auf den
 * Karten ist für alle dieselbe.
 */
export function CockpitBoard({
  phaseKey,
  board,
  filter,
  darfOrdnen,
  darfFreigeben,
  onReload,
  onOpen,
  onEinteilen,
}: {
  phaseKey: string
  board: Board
  filter: CockpitFilter
  darfOrdnen: boolean
  darfFreigeben: boolean
  onReload: () => Promise<void>
  onOpen: (stepId: string) => void
  onEinteilen: (stepIds: string[]) => void
}) {
  const terms = useTerminology()
  const [zoom, setZoom] = useState(1)
  const [klein, setKlein] = useState(false)
  const [auswahl, setAuswahl] = useState<Set<string>>(new Set())
  const [ziehen, setZiehen] = useState<Ziehen | null>(null)
  const [rueckgaengig, setRueckgaengig] = useState<CockpitLayoutSnapshot | null>(null)
  const [menu, setMenu] = useState<'arrange' | 'copy' | null>(null)
  const [ersetzen, setErsetzen] = useState(false)
  const [besitzer, setBesitzer] = useState<{ userId: string; name: string; cards: number }[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [lokal, setLokal] = useState<Record<string, { x: number | null; y: number | null; groupId: string | null }>>({})
  const [gruppenLokal, setGruppenLokal] = useState<Record<string, Partial<CockpitGroup>>>({})
  const [gruppenMenu, setGruppenMenu] = useState<string | null>(null)
  const flaeche = useRef<HTMLDivElement>(null)
  const eingang = useRef<HTMLDivElement>(null)
  const wurzel = useRef<HTMLDivElement>(null)
  // Auftraggeber 15.10.2026: Der Eingang lässt sich schmaler ziehen, damit das Board Platz bekommt — je Benutzer gemerkt.
  // Die Vorgabe entspricht der bisherigen festen Breite (18 rem) bei einem 1440er Fenster; Doppelklick auf den Rand stellt sie her.
  const teilung = useTeilung({ schluessel: 'split_work_cockpit', label: 'Breite des Eingangs', vorgabe: 25, min: 8, max: 50 })
  const hoeheBoard = useRestHoehe(wurzel)

  // Nach jedem Neuladen gilt wieder der Server — die lokal gemerkte Lage ist nur
  // die Brücke, bis die Antwort da ist. Sonst überdeckt sie, was der Server
  // beim Einsortieren in eine Gruppe neu gesetzt hat.
  useEffect(() => {
    setLokal({})
    setGruppenLokal({})
  }, [board])

  const hoehe = klein ? KLEIN_H : board.grid.cardHeight
  const breite = board.grid.cardWidth
  const raster = board.grid.raster

  // Die Lage einer Karte: was der Benutzer gerade gezogen hat, sonst der Server.
  const lage = useCallback(
    (k: CockpitCard) => lokal[k.stepId] ?? k.layout ?? { x: null, y: null, groupId: null },
    [lokal],
  )
  const gruppen = board.groups.map((g) => ({ ...g, ...gruppenLokal[g.id] }))
  const zugeklappt = new Set(gruppen.filter((g) => g.collapsed).map((g) => g.id))

  const imEingang = board.cards.filter((k) => lage(k).x === null || lage(k).y === null)
  const aufBoard = board.cards.filter((k) => lage(k).x !== null && lage(k).y !== null)

  const groesse = useMemo(() => {
    let w = 1600
    let h = 1000
    for (const k of aufBoard) {
      w = Math.max(w, (lage(k).x ?? 0) + breite + 400)
      h = Math.max(h, (lage(k).y ?? 0) + hoehe + 400)
    }
    for (const g of gruppen) {
      w = Math.max(w, g.x + g.w + 400)
      h = Math.max(h, g.y + g.h + 400)
    }
    return { w, h }
  }, [aufBoard, gruppen, lage, breite, hoehe])

  const auf = (n: number) => Math.round(n / raster) * raster

  /** Bildschirmpunkt → Board-Koordinate, mit Zoom und Scroll. */
  const punkt = (clientX: number, clientY: number) => {
    const r = flaeche.current!.getBoundingClientRect()
    return { x: (clientX - r.left) / zoom, y: (clientY - r.top) / zoom }
  }

  const gruppeUnter = (x: number, y: number) =>
    gruppen
      .filter((g) => !g.collapsed && x >= g.x && x <= g.x + g.w && y >= g.y + KOPF / 2 && y <= g.y + g.h)
      .sort((a, b) => a.w * a.h - b.w * b.h)[0] ?? null

  async function sicher(f: () => Promise<unknown>, neuLaden = true) {
    setFehler(null)
    try {
      await f()
      if (neuLaden) await onReload()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
      await onReload()
    }
  }

  // ── Ziehen ───────────────────────────────────────────────────────────────

  function karteGreifen(e: React.PointerEvent, k: CockpitCard, ausEingang: boolean) {
    if (e.button !== 0) return
    e.stopPropagation()
    if (e.ctrlKey || e.metaKey) {
      setAuswahl((a) => {
        const n = new Set(a)
        if (n.has(k.stepId)) n.delete(k.stepId)
        else n.add(k.stepId)
        return n
      })
      return
    }
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
    setZiehen({
      art: 'karte',
      stepId: k.stepId,
      startX: e.clientX,
      startY: e.clientY,
      greifX: (e.clientX - rect.left) / (ausEingang ? 1 : zoom),
      greifY: (e.clientY - rect.top) / (ausEingang ? 1 : zoom),
      bewegt: false,
      x: e.clientX,
      y: e.clientY,
      ausEingang,
    })
  }

  function bewegen(e: React.PointerEvent) {
    if (!ziehen) return
    if (ziehen.art === 'karte') {
      const bewegt = ziehen.bewegt || Math.abs(e.clientX - ziehen.startX) + Math.abs(e.clientY - ziehen.startY) > 4
      setZiehen({ ...ziehen, bewegt: bewegt && darfOrdnen, x: e.clientX, y: e.clientY })
    } else if (ziehen.art === 'gruppe') {
      setZiehen({ ...ziehen, dx: (e.clientX - ziehen.startX) / zoom, dy: (e.clientY - ziehen.startY) / zoom })
    } else if (ziehen.art === 'groesse') {
      setZiehen({ ...ziehen, dw: (e.clientX - ziehen.startX) / zoom, dh: (e.clientY - ziehen.startY) / zoom })
    } else if (ziehen.art === 'rahmen') {
      const p = punkt(e.clientX, e.clientY)
      setZiehen({ ...ziehen, x1: p.x, y1: p.y })
    }
  }

  async function loslassen(e: React.PointerEvent) {
    const z = ziehen
    setZiehen(null)
    if (!z) return

    if (z.art === 'karte') {
      if (!z.bewegt) {
        onOpen(z.stepId)
        return
      }
      const ueberEingang = (() => {
        const r = eingang.current?.getBoundingClientRect()
        return !!r && e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom
      })()
      if (ueberEingang) {
        setLokal((l) => ({ ...l, [z.stepId]: { x: null, y: null, groupId: null } }))
        await sicher(() => placeCard(phaseKey, z.stepId, { x: null, y: null, groupId: null }))
        return
      }
      const p = punkt(e.clientX, e.clientY)
      const x = Math.max(0, auf(p.x - z.greifX))
      const y = Math.max(0, auf(p.y - z.greifY))
      const karte = board.cards.find((k) => k.stepId === z.stepId)!
      const alt = lage(karte)

      // Mehrfachauswahl: alle gewählten Karten auf dem Board um denselben Versatz.
      if (auswahl.has(z.stepId) && auswahl.size > 1 && alt.x !== null && alt.y !== null) {
        const dx = x - alt.x
        const dy = y - alt.y
        const ids = [...auswahl].filter((id) => {
          const k = board.cards.find((c) => c.stepId === id)
          return k && lage(k).x !== null
        })
        setLokal((l) => {
          const n = { ...l }
          for (const id of ids) {
            const k = board.cards.find((c) => c.stepId === id)!
            const la = lage(k)
            n[id] = { ...la, x: (la.x ?? 0) + dx, y: (la.y ?? 0) + dy }
          }
          return n
        })
        await sicher(() => placeCards(phaseKey, { stepIds: ids, dx, dy }))
        return
      }

      const gruppe = gruppeUnter(x + breite / 2, y + hoehe / 2)
      setLokal((l) => ({ ...l, [z.stepId]: { x, y, groupId: gruppe?.id ?? null } }))
      await sicher(() => placeCard(phaseKey, z.stepId, { x, y, groupId: gruppe?.id ?? null }), false)
      return
    }

    if (z.art === 'gruppe') {
      const g = gruppen.find((x) => x.id === z.id)!
      const x = Math.max(0, auf(z.x0 + z.dx))
      const y = Math.max(0, auf(z.y0 + z.dy))
      if (x === g.x && y === g.y) return
      const dx = x - g.x
      const dy = y - g.y
      setGruppenLokal((gl) => ({ ...gl, [z.id]: { ...gl[z.id], x, y } }))
      setLokal((l) => {
        const n = { ...l }
        for (const k of board.cards) {
          const la = lage(k)
          if (la.groupId === z.id && la.x !== null && la.y !== null) n[k.stepId] = { ...la, x: la.x + dx, y: la.y + dy }
        }
        return n
      })
      await sicher(() => updateCockpitGroup(phaseKey, z.id, { x, y }), false)
      return
    }

    if (z.art === 'groesse') {
      const g = gruppen.find((x) => x.id === z.id)!
      const w = Math.max(breite + 32, auf(z.w0 + z.dw))
      const h = Math.max(KOPF + 64, auf(z.h0 + z.dh))
      setGruppenLokal((gl) => ({ ...gl, [z.id]: { ...gl[z.id], w, h } }))
      if (w !== g.w || h !== g.h) await sicher(() => updateCockpitGroup(phaseKey, z.id, { w, h }), false)
      return
    }

    if (z.art === 'rahmen') {
      const [x0, x1] = [Math.min(z.x0, z.x1), Math.max(z.x0, z.x1)]
      const [y0, y1] = [Math.min(z.y0, z.y1), Math.max(z.y0, z.y1)]
      if (x1 - x0 < 8 && y1 - y0 < 8) {
        setAuswahl(new Set())
        return
      }
      const getroffen = aufBoard.filter((k) => {
        const la = lage(k)
        if (la.groupId && zugeklappt.has(la.groupId)) return false
        return la.x! < x1 && la.x! + breite > x0 && la.y! < y1 && la.y! + hoehe > y0
      })
      setAuswahl(new Set(getroffen.map((k) => k.stepId)))
    }
  }

  // ── Aktionen ─────────────────────────────────────────────────────────────

  async function neueGruppe() {
    const name = await frage('Name der Gruppe', 'KW …')
    if (!name?.trim()) return
    const r = flaeche.current?.parentElement
    const x = auf((r?.scrollLeft ?? 0) / zoom + 32)
    const y = auf((r?.scrollTop ?? 0) / zoom + 32)
    await sicher(() => createCockpitGroup(phaseKey, { name: name.trim(), x, y, stepIds: auswahl.size ? [...auswahl] : undefined }))
    setAuswahl(new Set())
  }

  async function anordnen(by: AutoArrangeMode) {
    setMenu(null)
    await sicher(async () => {
      const r = await autoArrange(phaseKey, by, ersetzen, filter)
      setRueckgaengig(r.previous)
      setLokal({})
      setGruppenLokal({})
    })
  }

  async function zuruecknehmen() {
    if (!rueckgaengig) return
    await sicher(async () => {
      await restoreLayout(phaseKey, rueckgaengig)
      setRueckgaengig(null)
      setLokal({})
      setGruppenLokal({})
    })
  }

  async function uebernehmenMenu() {
    setMenu(menu === 'copy' ? null : 'copy')
    if (menu !== 'copy') {
      try {
        setBesitzer(await fetchLayoutOwners(phaseKey))
      } catch (e) {
        setFehler(apiErrorText(e, 'Laden fehlgeschlagen.'))
      }
    }
  }

  const gewaehlteIds = [...auswahl]
  const knopf = 'rounded-md border border-border px-2 py-1 text-xs hover:bg-muted disabled:opacity-40'

  const gezogeneKarte = ziehen?.art === 'karte' && ziehen.bewegt ? board.cards.find((k) => k.stepId === ziehen.stepId) : null

  return (
    <div ref={wurzel} className="flex h-[calc(100vh-18rem)] min-h-[28rem] flex-col gap-2" style={hoeheBoard ? { height: hoeheBoard } : undefined}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 rounded-md border border-border">
          <button type="button" className="p-1 hover:bg-muted" onClick={() => setZoom((z) => Math.max(0.5, Math.round((z - 0.1) * 10) / 10))} aria-label="verkleinern">
            <Minus className="h-3.5 w-3.5" />
          </button>
          <span className="w-10 text-center text-xs tabular-nums">{Math.round(zoom * 100)} %</span>
          <button type="button" className="p-1 hover:bg-muted" onClick={() => setZoom((z) => Math.min(1.5, Math.round((z + 0.1) * 10) / 10))} aria-label="vergrößern">
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" checked={klein} onChange={(e) => setKlein(e.target.checked)} /> klein
        </label>

        {darfOrdnen && (
          <>
            <button type="button" className={knopf} onClick={() => void neueGruppe()}>
              + Gruppe{auswahl.size ? ` mit ${auswahl.size}` : ''}
            </button>
            <div className="relative">
              <button type="button" className={knopf} onClick={() => setMenu(menu === 'arrange' ? null : 'arrange')}>
                Automatisch anordnen ▾
              </button>
              {menu === 'arrange' && (
                <div className="absolute left-0 top-full z-50 mt-1 w-56 rounded-md border border-border p-1 text-sm shadow-lg" style={OVERLAY}>
                  {(
                    [
                      ['DUE_WEEK', 'nach Fälligkeit (Woche)'],
                      ['PROJECT', `nach ${terms.project}`],
                      ['USER', 'nach Mitarbeiter'],
                      ['STATUS', 'nach Status'],
                    ] as [AutoArrangeMode, string][]
                  ).map(([k, l]) => (
                    <button key={k} type="button" className="block w-full rounded px-2 py-1 text-left hover:bg-muted" onClick={() => void anordnen(k)}>
                      {l}
                    </button>
                  ))}
                  <label className="mt-1 flex items-center gap-1 border-t border-border px-2 pt-1 text-xs text-muted-foreground">
                    <input type="checkbox" checked={ersetzen} onChange={(e) => setErsetzen(e.target.checked)} /> bestehende Gruppen ersetzen
                  </label>
                </div>
              )}
            </div>
            {rueckgaengig && (
              <button type="button" className={`${knopf} flex items-center gap-1`} onClick={() => void zuruecknehmen()}>
                <Undo2 className="h-3 w-3" /> Rückgängig
              </button>
            )}
            <div className="relative">
              <button type="button" className={knopf} onClick={() => void uebernehmenMenu()}>
                Anordnung übernehmen ▾
              </button>
              {menu === 'copy' && (
                <div className="absolute left-0 top-full z-50 mt-1 w-64 rounded-md border border-border p-1 text-sm shadow-lg" style={OVERLAY}>
                  {besitzer.length === 0 && <p className="px-2 py-1 text-xs text-muted-foreground">Niemand sonst hat hier eine Anordnung.</p>}
                  {besitzer.map((b) => (
                    <button
                      key={b.userId}
                      type="button"
                      className="block w-full rounded px-2 py-1 text-left hover:bg-muted"
                      onClick={async () => {
                        setMenu(null)
                        if (!(await bestaetige(`Die eigene Anordnung durch die von ${b.name} ersetzen?`))) return
                        void sicher(async () => {
                          const r = await copyLayout(phaseKey, b.userId)
                          setRueckgaengig(r.previous)
                          setLokal({})
                          setGruppenLokal({})
                        })
                      }}
                    >
                      {b.name} <span className="text-xs text-muted-foreground">({b.cards} Karten)</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {auswahl.size > 0 && (
          <div className="ml-auto flex flex-wrap items-center gap-2 rounded-md bg-primary/10 px-2 py-1 text-xs">
            <span>{auswahl.size} gewählt</span>
            {darfOrdnen && (
              <button type="button" className={knopf} onClick={() => onEinteilen(gewaehlteIds)}>
                einteilen
              </button>
            )}
            {darfFreigeben && (
              <button type="button" className={knopf} onClick={() => void sicher(() => releaseWorkSteps(gewaehlteIds))}>
                freigeben
              </button>
            )}
            {darfOrdnen && gruppen.length > 0 && (
              <select
                className="rounded border border-border bg-background px-1 py-0.5"
                value=""
                onChange={(e) => {
                  const ziel = e.target.value
                  if (!ziel) return
                  void sicher(() => placeCards(phaseKey, { stepIds: gewaehlteIds, groupId: ziel === '-' ? null : ziel }))
                  setLokal({})
                }}
              >
                <option value="">in Gruppe …</option>
                {gruppen.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
                <option value="-">aus der Gruppe nehmen</option>
              </select>
            )}
            <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => setAuswahl(new Set())}>
              ✕
            </button>
          </div>
        )}
      </div>

      {fehler && <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>}

      <div ref={teilung.behaelter} className={`flex min-h-0 flex-1 ${teilung.zieht ? 'select-none' : ''}`} onPointerMove={bewegen} onPointerUp={(e) => void loslassen(e)}>
        <div ref={eingang} className="flex min-w-0 shrink-0 flex-col rounded-lg border border-dashed border-border bg-muted/30" style={{ width: `${teilung.anteil}%` }}>
          <div className="border-b border-border px-3 py-1.5 text-xs font-medium text-muted-foreground">Eingang · {imEingang.length}</div>
          <div className="flex-1 space-y-2 overflow-y-auto p-2">
            {imEingang.map((k) => (
              <div
                key={k.stepId}
                style={{ height: hoehe }}
                className={`cursor-grab touch-none select-none ${ziehen?.art === 'karte' && ziehen.stepId === k.stepId && ziehen.bewegt ? 'opacity-30' : ''}`}
                onPointerDown={(e) => karteGreifen(e, k, true)}
              >
                <CockpitCardView karte={k} klein={klein} gewaehlt={auswahl.has(k.stepId)} onEinteilen={darfOrdnen ? () => onEinteilen([k.stepId]) : undefined} />
              </div>
            ))}
            {imEingang.length === 0 && <p className="px-1 text-xs text-muted-foreground">Alle Karten liegen auf dem Board.</p>}
          </div>
        </div>

        {/* Der Rand zwischen Eingang und Board — er nimmt den Platz des früheren Abstands ein. */}
        <div {...teilung.griff} className="group flex w-2 shrink-0 cursor-col-resize touch-none justify-center focus:outline-none">
          <span className={`h-full w-1 rounded-full transition-colors group-hover:bg-primary/60 group-focus-visible:bg-primary ${teilung.zieht ? 'bg-primary' : 'bg-transparent'}`} />
        </div>

        <div className="relative min-w-0 flex-1 overflow-auto rounded-lg border border-border bg-[radial-gradient(circle,hsl(var(--border))_1px,transparent_1px)] [background-size:16px_16px]">
          <div style={{ width: groesse.w * zoom, height: groesse.h * zoom }} className="relative">
            <div
              ref={flaeche}
              className="absolute left-0 top-0 origin-top-left"
              style={{ width: groesse.w, height: groesse.h, transform: `scale(${zoom})` }}
              onPointerDown={(e) => {
                if (e.button !== 0 || e.target !== e.currentTarget) return
                const p = punkt(e.clientX, e.clientY)
                ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
                setZiehen({ art: 'rahmen', x0: p.x, y0: p.y, x1: p.x, y1: p.y })
              }}
            >
              {gruppen.map((g) => {
                const verschoben = ziehen?.art === 'gruppe' && ziehen.id === g.id
                const gx = verschoben ? g.x + ziehen.dx : g.x
                const gy = verschoben ? g.y + ziehen.dy : g.y
                const gr = ziehen?.art === 'groesse' && ziehen.id === g.id
                const gw = gr ? Math.max(breite + 32, g.w + ziehen.dw) : g.w
                const gh = gr ? Math.max(KOPF + 64, g.h + ziehen.dh) : g.h
                const anzahl = board.cards.filter((k) => lage(k).groupId === g.id).length
                return (
                  <div
                    key={g.id}
                    className={`absolute rounded-lg border-2 ${NOTE_COLOR_CLASSES[g.color] ?? ''} bg-opacity-40`}
                    style={{ left: gx, top: gy, width: gw, height: g.collapsed ? KOPF - 8 : gh }}
                  >
                    <div
                      className={`flex h-10 items-center gap-1 px-2 text-sm font-medium ${darfOrdnen ? 'cursor-move' : ''} touch-none select-none`}
                      onPointerDown={(e) => {
                        if (!darfOrdnen || e.button !== 0) return
                        e.stopPropagation()
                        ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
                        setZiehen({ art: 'gruppe', id: g.id, startX: e.clientX, startY: e.clientY, x0: g.x, y0: g.y, dx: 0, dy: 0 })
                      }}
                    >
                      <button
                        type="button"
                        onPointerDown={(e) => e.stopPropagation()}
                        onClick={() => {
                          setGruppenLokal((gl) => ({ ...gl, [g.id]: { ...gl[g.id], collapsed: !g.collapsed } }))
                          if (darfOrdnen) void sicher(() => updateCockpitGroup(phaseKey, g.id, { collapsed: !g.collapsed }), false)
                        }}
                        aria-label={g.collapsed ? 'aufklappen' : 'zuklappen'}
                      >
                        {g.collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </button>
                      <span
                        className="min-w-0 flex-1 truncate"
                        title={g.name}
                        onDoubleClick={async () => {
                          if (!darfOrdnen) return
                          const name = await frage('Name der Gruppe', g.name)
                          if (name?.trim()) void sicher(() => updateCockpitGroup(phaseKey, g.id, { name: name.trim() }))
                        }}
                      >
                        {g.name}
                      </span>
                      <span className="text-xs text-muted-foreground">{anzahl}</span>
                      {darfOrdnen && (
                        <div className="relative" onPointerDown={(e) => e.stopPropagation()}>
                          <button
                            type="button"
                            className="rounded px-1.5 hover:bg-background/60"
                            aria-label="Gruppe bearbeiten"
                            onClick={() => setGruppenMenu(gruppenMenu === g.id ? null : g.id)}
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </button>
                          {gruppenMenu === g.id && (
                            <div className="absolute right-0 top-full z-50 mt-1 w-48 rounded-md border border-border p-1 text-sm font-normal shadow-lg" style={OVERLAY}>
                              <div className="flex gap-1.5 px-2 py-1.5">
                                {FARBEN.map((f) => (
                                  <button
                                    key={f}
                                    type="button"
                                    aria-label={`Farbe ${f}`}
                                    className={`h-4 w-4 rounded-full border ${NOTE_COLOR_CLASSES[f]} ${g.color === f ? 'ring-2 ring-foreground' : ''}`}
                                    onClick={() => void sicher(() => updateCockpitGroup(phaseKey, g.id, { color: f }))}
                                  />
                                ))}
                              </div>
                              {[
                                ['alle einteilen', () => onEinteilen(board.cards.filter((k) => lage(k).groupId === g.id).map((k) => k.stepId))],
                                ...(darfFreigeben
                                  ? [['alle freigeben', () => void sicher(() => releaseWorkSteps(board.cards.filter((k) => lage(k).groupId === g.id).map((k) => k.stepId)))] as const]
                                  : []),
                                [
                                  'umbenennen',
                                  async () => {
                                    const name = await frage('Name der Gruppe', g.name)
                                    if (name?.trim()) void sicher(() => updateCockpitGroup(phaseKey, g.id, { name: name.trim() }))
                                  },
                                ],
                                [
                                  'auflösen (Karten bleiben)',
                                  async () => {
                                    if (await bestaetige(`Gruppe „${g.name}" auflösen? Die Karten bleiben liegen.`)) void sicher(() => deleteCockpitGroup(phaseKey, g.id))
                                  },
                                ],
                              ].map(([label, f]) => (
                                <button
                                  key={label as string}
                                  type="button"
                                  className="block w-full rounded px-2 py-1 text-left hover:bg-muted"
                                  onClick={() => {
                                    setGruppenMenu(null)
                                    ;(f as () => void)()
                                  }}
                                >
                                  {label as string}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {darfOrdnen && !g.collapsed && (
                      <div
                        className="absolute bottom-0 right-0 h-3 w-3 cursor-se-resize touch-none"
                        onPointerDown={(e) => {
                          e.stopPropagation()
                          ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
                          setZiehen({ art: 'groesse', id: g.id, startX: e.clientX, startY: e.clientY, w0: g.w, h0: g.h, dw: 0, dh: 0 })
                        }}
                      />
                    )}
                  </div>
                )
              })}

              {aufBoard.map((k) => {
                const la = lage(k)
                if (la.groupId && zugeklappt.has(la.groupId)) return null
                const gv = ziehen?.art === 'gruppe' && la.groupId === ziehen.id ? { dx: ziehen.dx, dy: ziehen.dy } : { dx: 0, dy: 0 }
                const wird = ziehen?.art === 'karte' && ziehen.stepId === k.stepId && ziehen.bewegt
                return (
                  <div
                    key={k.stepId}
                    className={`absolute cursor-grab touch-none select-none ${wird ? 'opacity-30' : ''}`}
                    style={{ left: la.x! + gv.dx, top: la.y! + gv.dy, width: breite, height: hoehe }}
                    onPointerDown={(e) => karteGreifen(e, k, false)}
                  >
                    <CockpitCardView karte={k} klein={klein} gewaehlt={auswahl.has(k.stepId)} onEinteilen={darfOrdnen ? () => onEinteilen([k.stepId]) : undefined} />
                  </div>
                )
              })}

              {ziehen?.art === 'rahmen' && (
                <div
                  className="pointer-events-none absolute border border-primary bg-primary/10"
                  style={{
                    left: Math.min(ziehen.x0, ziehen.x1),
                    top: Math.min(ziehen.y0, ziehen.y1),
                    width: Math.abs(ziehen.x1 - ziehen.x0),
                    height: Math.abs(ziehen.y1 - ziehen.y0),
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {gezogeneKarte && ziehen?.art === 'karte' && (
        <div
          className="pointer-events-none fixed z-50 opacity-90 shadow-xl"
          style={{
            left: ziehen.x - ziehen.greifX * zoom,
            top: ziehen.y - ziehen.greifY * zoom,
            width: breite * zoom,
            height: hoehe * zoom,
          }}
        >
          <div style={{ width: breite, height: hoehe, transform: `scale(${zoom})`, transformOrigin: '0 0' }}>
            <CockpitCardView karte={gezogeneKarte} klein={klein} />
          </div>
        </div>
      )}

      {board.truncated && <p className="text-xs text-muted-foreground">Es sind mehr als 1.000 Karten — bitte filtern.</p>}
    </div>
  )
}

/**
 * **Die Höhe bis zum unteren Rand des Inhaltsbereichs.** Klappt darüber die Filterleiste zu
 * (oder wächst sie um eine Zeile), wandert das Board nach oben und nimmt den Platz mit — eine
 * feste Höhe ließ ihn unten leer stehen. Gemessen wird gegen den scrollenden `<main>`, so als
 * stünde er ganz oben; die `min-h` am Board gilt weiter für kleine Fenster.
 */
function useRestHoehe(ref: React.RefObject<HTMLElement>): number | null {
  const [hoehe, setHoehe] = useState<number | null>(null)
  useLayoutEffect(() => {
    const el = ref.current
    const rahmen = el?.closest('main')
    if (!el || !rahmen) return
    const messen = () => {
      const oben = el.getBoundingClientRect().top - rahmen.getBoundingClientRect().top + rahmen.scrollTop
      setHoehe(Math.round(rahmen.clientHeight - oben - UNTEN))
    }
    messen()
    // Die Seite darüber ändert ihre Höhe beim Ein- und Ausklappen der Leiste, das Fenster beim Verkleinern.
    const beobachter = new ResizeObserver(messen)
    if (el.parentElement) beobachter.observe(el.parentElement)
    beobachter.observe(rahmen)
    return () => beobachter.disconnect()
  }, [ref])
  return hoehe
}
